import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Stepper,
  Step,
  StepLabel,
  Switch,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormLabel,
  Divider,
  Avatar,
  Tooltip,
  Chip,
} from '@mui/material';
import {
  ArrowBack,
  Description,
  AttachMoney,
  CreditCard,
  Article,
  DirectionsCar,
  Restaurant,
  Business,
  Home,
  ShoppingCart,
  LocalShipping,
  Build,
  AccountBalance,
  Apartment,
  LocalHospital,
  School,
  Park,
  Agriculture,
  WaterDrop,
  LocalBar,
  Storefront,
  Engineering,
  Visibility,
  Info,
  QrCode2,
} from '@mui/icons-material';
import { ModuleLayout } from '../../components/Layout/ModuleLayout';
import apiClient from '../../api/client';
import { moduleService, moduleServiceService } from '../../services';
import { colors } from '../../theme/theme';
import { countyProfileApi, CountySignatory } from '../../api/countyProfile';

export const EditServicePage = () => {
  const { id, serviceId } = useParams<{ id: string; serviceId: string }>();
  const navigate = useNavigate();
  const [module, setModule] = useState<any>(null);
  const [categories, setCategories] = useState<any[]>([]);
  const [billingCycles, setBillingCycles] = useState<any[]>([]);
  const [signatories, setSignatories] = useState<CountySignatory[]>([]);
  const [countyProfile, setCountyProfile] = useState<{ name: string; logo?: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category_id: '',
    billing_type: 'periodical',
    billing_cycle_id: '',
    icon: 'Description',
    has_printable: false,
    printable_type: '',
    printable_template: '',
    printable_config: {
      logo_position: 'center',
      qr_position: 'bottom-right',
      qr_payload: 'verify',
      signature_count: '1',
      signature_titles: ['Authorized Officer'],
      selected_signatory_ids: [] as number[],
      design_theme: 'classic',
      paper_type: 'plain',
      document_size: 'a4',
      watermark: true,
      security_seal: true,
      primary_color: '#6c3fc5',
      sticker_shape: 'circle',
      document_content: '<p>This is to certify that <strong>{{applicant_name}}</strong> has successfully registered for <strong>{{service_name}}</strong>. This document remains valid until <strong>{{expiry_date}}</strong>.</p>',
    },
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });

  const steps = ['General Info', 'Service Identity', 'Document Designer'];

  // Get preview dimensions based on document size
  const getPreviewDimensions = () => {
    const size = formData.printable_config.document_size;
    // Return width, aspect ratio, and scale factor for preview scaling
    switch (size) {
      case 'a4':
        return { width: '100%', aspectRatio: '210/297', fontSize: '1', padding: 4, scale: 1 }; // A4: 210mm × 297mm
      case 'a5':
        return { width: '85%', aspectRatio: '148/210', fontSize: '0.9', padding: 3, scale: 0.85 }; // A5: 148mm × 210mm
      case 'sticker-large':
        return { width: '70%', aspectRatio: '150/100', fontSize: '0.75', padding: 2.5, scale: 0.65 }; // Large sticker
      case 'sticker-medium':
        return { width: '55%', aspectRatio: '100/70', fontSize: '0.65', padding: 2, scale: 0.5 }; // Medium sticker
      case 'sticker-small':
        return { width: '45%', aspectRatio: '70/50', fontSize: '0.55', padding: 1.5, scale: 0.4 }; // Small sticker
      default:
        return { width: '100%', aspectRatio: '210/297', fontSize: '1', padding: 4, scale: 1 };
    }
  };

  // Calculate preview dates based on billing cycle
  const getPreviewDates = () => {
    const today = new Date();
    const dateIssued = today.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

    let expiryDate = new Date(today);
    const selectedCycle = billingCycles.find(c => c.id === Number(formData.billing_cycle_id));

    if (selectedCycle) {
      // Add period based on billing cycle
      const period = selectedCycle.period_value || 12; // Default to 12 months if not set
      const periodType = selectedCycle.period_type || 'months'; // months, years, days

      if (periodType === 'months') {
        expiryDate.setMonth(expiryDate.getMonth() + period);
      } else if (periodType === 'years') {
        expiryDate.setFullYear(expiryDate.getFullYear() + period);
      } else if (periodType === 'days') {
        expiryDate.setDate(expiryDate.getDate() + period);
      }
    } else {
      // Default to 12 months if no cycle selected
      expiryDate.setMonth(expiryDate.getMonth() + 12);
    }

    const formattedExpiryDate = expiryDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

    return { dateIssued, expiryDate: formattedExpiryDate };
  };

  const iconOptions = [
    { name: 'Description', icon: Description },
    { name: 'AttachMoney', icon: AttachMoney },
    { name: 'CreditCard', icon: CreditCard },
    { name: 'Article', icon: Article },
    { name: 'DirectionsCar', icon: DirectionsCar },
    { name: 'Restaurant', icon: Restaurant },
    { name: 'Business', icon: Business },
    { name: 'Home', icon: Home },
    { name: 'ShoppingCart', icon: ShoppingCart },
    { name: 'LocalShipping', icon: LocalShipping },
    { name: 'Build', icon: Build },
    { name: 'AccountBalance', icon: AccountBalance },
    { name: 'Apartment', icon: Apartment },
    { name: 'LocalHospital', icon: LocalHospital },
    { name: 'School', icon: School },
    { name: 'Park', icon: Park },
    { name: 'Agriculture', icon: Agriculture },
    { name: 'WaterDrop', icon: WaterDrop },
    { name: 'LocalBar', icon: LocalBar },
    { name: 'Storefront', icon: Storefront },
    { name: 'Engineering', icon: Engineering },
  ];

  const printableTemplates: Record<string, any[]> = {
    license: [
      { id: 'unified_business_permit', name: 'Unified Business Permit' },
      { id: 'single_business_permit', name: 'Single Business Permit' },
      { id: 'liquor_license', name: 'Liquor License' },
      { id: 'trade_license', name: 'General Trade License' },
    ],
    certificate: [
      { id: 'occupational_health', name: 'Occupational Health Certificate' },
      { id: 'fire_clearance', name: 'Fire Clearance Certificate' },
      { id: 'food_handling', name: 'Food Handling Certificate' },
      { id: 'compliance_cert', name: 'Certificate of Compliance' },
    ],
    sticker: [
      { id: 'branded_vehicle_sticker', name: 'Branded Vehicle Sticker' },
      { id: 'parking_sticker', name: 'Monthly Parking Sticker' },
      { id: 'advertisement_sticker', name: 'Advertisement Sticker' },
      { id: 'tuktuk_sticker', name: 'Tuk-Tuk/Motorcycle Sticker' },
    ],
  };

  useEffect(() => {
    fetchData();
  }, [id, serviceId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [moduleRes, servicesRes, categoriesRes, cyclesRes, profileData] = await Promise.all([
        moduleService.getModule(Number(id)),
        moduleServiceService.getAll(Number(id!)),
        apiClient.get(`/admin/county/modules/${id}/categories`),
        apiClient.get(`/admin/county/billing-cycles`),
        countyProfileApi.getProfile(),
      ]);

      if (moduleRes.status === 'success') {
        setModule(moduleRes.data);
      }

      // Find the specific service to edit
      if (servicesRes.status === 'success' && servicesRes.data) {
        const servicesData = servicesRes.data || [];
        const services = Array.isArray(servicesData) ? servicesData : [];
        const service = services.find((s: any) => s.id === parseInt(serviceId!));

        if (service) {
          setFormData({
            name: service.name || '',
            description: service.description || '',
            category_id: service.category_id?.toString() || '',
            billing_type: service.billing_type || 'periodical',
            billing_cycle_id: service.billing_cycle_id?.toString() || '',
            icon: service.icon || 'Description',
            has_printable: service.has_printable || false,
            printable_type: service.printable_type || '',
            printable_template: service.printable_template || '',
            printable_config: {
              logo_position: service.printable_config?.logo_position || 'center',
              qr_position: service.printable_config?.qr_position || 'bottom-right',
              qr_payload: service.printable_config?.qr_payload || 'verify',
              signature_count: service.printable_config?.signature_count || '1',
              signature_titles: service.printable_config?.signature_titles || ['Authorized Officer'],
              selected_signatory_ids: (service.printable_config?.selected_signatory_ids || []) as number[],
              design_theme: service.printable_config?.design_theme || 'classic',
              paper_type: service.printable_config?.paper_type || 'plain',
              document_size: service.printable_config?.document_size || 'a4',
              watermark: service.printable_config?.watermark !== undefined ? service.printable_config.watermark : true,
              security_seal: service.printable_config?.security_seal !== undefined ? service.printable_config.security_seal : true,
              primary_color: service.printable_config?.primary_color || '#6c3fc5',
              sticker_shape: service.printable_config?.sticker_shape || 'circle',
              document_content: service.printable_config?.document_content || '<p>This is to certify that <strong>{{applicant_name}}</strong> has successfully registered for <strong>{{service_name}}</strong>. This document remains valid until <strong>{{expiry_date}}</strong>.</p>',
            },
          });
        } else {
          setError('Service not found');
        }
      }

      if (categoriesRes.status === 'success' && categoriesRes.data) {
        const categoriesData = categoriesRes.data?.data || categoriesRes.data || [];
        setCategories(Array.isArray(categoriesData) ? categoriesData : []);
      }
      if (cyclesRes.status === 'success') {
        const cyclesData = cyclesRes.data?.data || cyclesRes.data || [];
        setBillingCycles(Array.isArray(cyclesData) ? cyclesData : []);
      }
      // Set county profile (name and logo)
      if (profileData) {
        setCountyProfile({
          name: profileData.name || '',
          logo: profileData.logo || ''
        });
        // Set signatories - filter only active ones
        if (profileData.signatories && Array.isArray(profileData.signatories)) {
          setSignatories(profileData.signatories.filter((s: CountySignatory) => s.is_active));
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const updatePrintableConfig = (key: string, value: any) => {
    setFormData({
      ...formData,
      printable_config: {
        ...formData.printable_config,
        [key]: value,
      },
    });
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      const data = {
        name: formData.name,
        description: formData.description,
        category: formData.category_id ? parseInt(formData.category_id) : undefined,
        billing_type: formData.billing_type,
        billing_cycle: formData.billing_type === 'periodical' && formData.billing_cycle_id
          ? parseInt(formData.billing_cycle_id)
          : null,
        icon: formData.icon,
        has_printable: formData.has_printable,
        printable_type: formData.printable_type,
        printable_template: formData.printable_template,
        printable_config: formData.printable_config,
      };

      const response = await moduleServiceService.update(Number(id!), Number(serviceId!), data);
      if (response.status === 'success') {
        const message = (response as any).message || 'Service updated successfully';
        setSnackbar({
          open: true,
          message,
          severity: 'success',
        });
        setTimeout(() => {
          navigate(`/modules/${id}/services`);
        }, 1000);
      }
    } catch (err: any) {
      console.error('Failed to save service:', err);
      const errorMessage = err.response?.data?.message || err.response?.data?.errors || 'Failed to save service';
      setSnackbar({
        open: true,
        message: typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage),
        severity: 'error',
      });
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <ModuleLayout module={module} moduleId={id!}>
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      </ModuleLayout>
    );
  }

  return (
    <ModuleLayout module={module} moduleId={id!}>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#1f2937', mb: 1 }}>
              Edit Service
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Update service configuration and settings
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={() => navigate(`/modules/${id}/services`)}
            sx={{
              borderColor: '#cbd5e1',
              color: '#64748b',
              '&:hover': {
                borderColor: '#94a3b8',
                bgcolor: '#f8fafc',
              },
            }}
          >
            Back to Services
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Card sx={{ borderRadius: 4 }}>
          <CardContent sx={{ p: 4 }}>
            <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {/* Step 1: General Info */}
            {activeStep === 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2" fontWeight={600}>Phase 1: Foundation</Typography>
                  <Typography variant="caption">Service Core & Billing</Typography>
                </Alert>

                <TextField
                  fullWidth
                  required
                  label="Service Name"
                  placeholder="e.g., Business Permit"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />

                <TextField
                  fullWidth
                  required
                  label="Description"
                  multiline
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />

                <FormControl fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={formData.category_id}
                    label="Category"
                    onChange={(e) => setFormData({ ...formData, category_id: e.target.value as string })}
                  >
                    <MenuItem value="">
                      <em>Select Category</em>
                    </MenuItem>
                    {categories.map((cat) => (
                      <MenuItem key={cat.id} value={cat.id.toString()}>
                        {cat.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <FormControl fullWidth required>
                    <InputLabel>Billing Type</InputLabel>
                    <Select
                      value={formData.billing_type}
                      label="Billing Type"
                      onChange={(e) => setFormData({ ...formData, billing_type: e.target.value, billing_cycle_id: e.target.value === 'one_off' ? '' : formData.billing_cycle_id })}
                    >
                      <MenuItem value="periodical">Periodical</MenuItem>
                      <MenuItem value="one_off">One-Off</MenuItem>
                    </Select>
                  </FormControl>

                  {formData.billing_type === 'periodical' && (
                    <FormControl fullWidth required>
                      <InputLabel>Billing Cycle</InputLabel>
                      <Select
                        value={formData.billing_cycle_id}
                        label="Billing Cycle"
                        onChange={(e) => setFormData({ ...formData, billing_cycle_id: e.target.value as string })}
                      >
                        <MenuItem value="">
                          <em>Select Billing Cycle</em>
                        </MenuItem>
                        {billingCycles.map((cycle) => (
                          <MenuItem key={cycle.id} value={cycle.id.toString()}>
                            {cycle.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </Box>
              </Box>
            )}

            {/* Step 2: Service Identity */}
            {activeStep === 1 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
                <Alert severity="info">
                  <Typography variant="body2" fontWeight={600}>Phase 2: Identity</Typography>
                  <Typography variant="caption">Service Branding</Typography>
                </Alert>

                <FormLabel component="legend">Service Icon</FormLabel>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 2 }}>
                  {iconOptions.map((option) => {
                    const IconComponent = option.icon;
                    return (
                      <Card
                        key={option.name}
                        onClick={() => setFormData({ ...formData, icon: option.name })}
                        sx={{
                          p: 2,
                          cursor: 'pointer',
                          border: formData.icon === option.name ? `2px solid ${colors.primary.main}` : '2px solid transparent',
                          bgcolor: formData.icon === option.name ? colors.primary[50] : 'transparent',
                          '&:hover': { bgcolor: colors.primary[50] },
                          transition: 'all 0.2s',
                        }}
                      >
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ bgcolor: formData.icon === option.name ? colors.primary.main : '#e5e7eb', width: 48, height: 48 }}>
                            <IconComponent sx={{ color: formData.icon === option.name ? 'white' : '#6b7280' }} />
                          </Avatar>
                          <Typography variant="caption" align="center" sx={{ fontWeight: formData.icon === option.name ? 700 : 400 }}>
                            {option.name}
                          </Typography>
                        </Box>
                      </Card>
                    );
                  })}
                </Box>
              </Box>
            )}

            {/* Step 3: Document Designer */}
            {activeStep === 2 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
                <Alert severity="success" icon={<Info />}>
                  <Typography variant="body2" fontWeight={600}>Phase 3: Document Designer</Typography>
                  <Typography variant="caption">Design printable documents that will be generated for this service. All changes are previewed in real-time on the right.</Typography>
                </Alert>

                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.has_printable}
                      onChange={(e) => {
                        const isEnabled = e.target.checked;
                        setFormData({
                          ...formData,
                          has_printable: isEnabled,
                          printable_type: isEnabled ? (formData.printable_type || 'certificate') : formData.printable_type,
                          printable_template: isEnabled ? (formData.printable_template || 'business_permit') : formData.printable_template
                        });
                      }}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={600}>Enable Printable Document</Typography>
                      <Typography variant="caption" color="text.secondary">Generate certificates, licenses, or stickers for this service</Typography>
                    </Box>
                  }
                />

                {formData.has_printable && (
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
                    {/* Left Column: Configuration */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 3, bgcolor: '#f9fafb', borderRadius: 2 }}>
                      <Typography variant="subtitle1" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Info fontSize="small" />
                        Document Settings
                      </Typography>
                      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                        <FormControl fullWidth>
                          <InputLabel>Document Type *</InputLabel>
                          <Select
                            value={formData.printable_type}
                            label="Document Type *"
                            onChange={(e) => setFormData({ ...formData, printable_type: e.target.value, printable_template: '' })}
                          >
                            <MenuItem value="">Select Type</MenuItem>
                            <MenuItem value="license">License (Permit)</MenuItem>
                            <MenuItem value="certificate">Certificate</MenuItem>
                            <MenuItem value="sticker">Sticker / Tag</MenuItem>
                          </Select>
                          <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5, ml: 1.5 }}>
                            Choose: License for permits, Certificate for clearances, Sticker for tags
                          </Typography>
                        </FormControl>

                        <FormControl fullWidth disabled={!formData.printable_type}>
                          <InputLabel>Template Style *</InputLabel>
                          <Select
                            value={formData.printable_template}
                            label="Template Style *"
                            onChange={(e) => setFormData({ ...formData, printable_template: e.target.value })}
                          >
                            <MenuItem value="">Select Template</MenuItem>
                            {formData.printable_type && printableTemplates[formData.printable_type]?.map((template) => (
                              <MenuItem key={template.id} value={template.id}>
                                {template.name}
                              </MenuItem>
                            ))}
                          </Select>
                          <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5, ml: 1.5 }}>
                            Pre-designed template with specific layout and formatting
                          </Typography>
                        </FormControl>
                      </Box>

                    <Divider />

                    <Typography variant="subtitle2" fontWeight={600}>Layout & Branding</Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2 }}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Logo Position</InputLabel>
                        <Select
                          value={formData.printable_config.logo_position}
                          label="Logo Position"
                          onChange={(e) => updatePrintableConfig('logo_position', e.target.value)}
                        >
                          <MenuItem value="center">Center</MenuItem>
                          <MenuItem value="left">Left</MenuItem>
                          <MenuItem value="right">Right</MenuItem>
                        </Select>
                      </FormControl>

                      <FormControl fullWidth size="small">
                        <InputLabel>QR Position</InputLabel>
                        <Select
                          value={formData.printable_config.qr_position}
                          label="QR Position"
                          onChange={(e) => updatePrintableConfig('qr_position', e.target.value)}
                        >
                          <MenuItem value="bottom-right">Bottom Right</MenuItem>
                          <MenuItem value="bottom-left">Bottom Left</MenuItem>
                          <MenuItem value="top-right">Top Right</MenuItem>
                        </Select>
                      </FormControl>

                      <FormControl fullWidth size="small">
                        <InputLabel>QR Payload</InputLabel>
                        <Select
                          value={formData.printable_config.qr_payload}
                          label="QR Payload"
                          onChange={(e) => updatePrintableConfig('qr_payload', e.target.value)}
                        >
                          <MenuItem value="verify">Verification URL</MenuItem>
                          <MenuItem value="meta">Serialized Metadata</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>

                    <Divider />

                    <Typography variant="subtitle2" fontWeight={600}>Signatories</Typography>
                    <FormControl component="fieldset">
                      <RadioGroup
                        row
                        value={formData.printable_config.signature_count}
                        onChange={(e) => {
                          const count = parseInt(e.target.value);
                          const titles = Array(count).fill('').map((_, i) => i === 0 ? 'Authorized Officer' : 'County Director');
                          const currentIds = formData.printable_config.selected_signatory_ids || [];
                          const newIds: number[] = Array(count).fill(0).map((_, i) => currentIds[i] || 0);
                          setFormData({
                            ...formData,
                            printable_config: {
                              ...formData.printable_config,
                              signature_count: e.target.value,
                              signature_titles: titles,
                              selected_signatory_ids: newIds,
                            },
                          });
                        }}
                      >
                        <FormControlLabel value="1" control={<Radio />} label="1 Signature" />
                        <FormControlLabel value="2" control={<Radio />} label="2 Signatures" />
                        <FormControlLabel value="3" control={<Radio />} label="3 Signatures" />
                      </RadioGroup>
                    </FormControl>

                    {signatories.length > 0 ? (
                      <>
                        <Alert severity="info" icon={false} sx={{ fontSize: '0.75rem', py: 0.5, mb: 2 }}>
                          <Typography variant="caption">
                            Select signatories from County Profile. {signatories.length} active signator{signatories.length === 1 ? 'y' : 'ies'} available.
                          </Typography>
                        </Alert>
                        {Array.from({ length: parseInt(formData.printable_config.signature_count) }).map((_, index) => (
                          <FormControl key={index} fullWidth size="small" sx={{ mb: index < parseInt(formData.printable_config.signature_count) - 1 ? 2 : 0 }}>
                            <InputLabel>Signatory {index + 1}</InputLabel>
                            <Select
                              value={(formData.printable_config.selected_signatory_ids || [])[index] || ''}
                              label={`Signatory ${index + 1}`}
                              onChange={(e) => {
                                const newIds: number[] = [...(formData.printable_config.selected_signatory_ids || [])];
                                newIds[index] = e.target.value as number;
                                updatePrintableConfig('selected_signatory_ids', newIds);
                              }}
                            >
                              <MenuItem value="">
                                <em>Select Signatory</em>
                              </MenuItem>
                              {signatories.map((sig) => (
                                <MenuItem key={sig.id} value={sig.id}>
                                  {sig.title} - {sig.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        ))}
                      </>
                    ) : (
                      formData.printable_config.signature_titles.map((title, index) => (
                        <TextField
                          key={index}
                          fullWidth
                          size="small"
                          label={`Signature ${index + 1} Title`}
                          value={title}
                          onChange={(e) => {
                            const newTitles = [...formData.printable_config.signature_titles];
                            newTitles[index] = e.target.value;
                            updatePrintableConfig('signature_titles', newTitles);
                          }}
                        />
                      ))
                    )}

                    <Divider />

                    <Typography variant="subtitle2" fontWeight={600}>Document Size</Typography>
                      <Alert severity="info" icon={false} sx={{ mb: 1, fontSize: '0.75rem', py: 0.5 }}>
                        <Typography variant="caption">
                          Choose size: A4 for full certificates/licenses, smaller sizes for vehicle stickers
                        </Typography>
                      </Alert>
                    <FormControl fullWidth size="small">
                      <InputLabel>Document Size</InputLabel>
                      <Select
                        value={formData.printable_config.document_size}
                        label="Document Size"
                        onChange={(e) => updatePrintableConfig('document_size', e.target.value)}
                      >
                        <MenuItem value="a4">A4 (210mm × 297mm) - Full Certificate/License</MenuItem>
                        <MenuItem value="a5">A5 (148mm × 210mm) - Half Page</MenuItem>
                        <MenuItem value="sticker-large">Large Sticker (150mm × 100mm) - Vehicle Windshield</MenuItem>
                        <MenuItem value="sticker-medium">Medium Sticker (100mm × 70mm) - Motorcycle</MenuItem>
                        <MenuItem value="sticker-small">Small Sticker (70mm × 50mm) - Compact Tag</MenuItem>
                      </Select>
                    </FormControl>

                    <Divider />

                    <Typography variant="subtitle2" fontWeight={600}>Style & Security</Typography>
                      <Alert severity="info" icon={false} sx={{ mb: 1, fontSize: '0.75rem', py: 0.5 }}>
                        <Typography variant="caption">
                          Choose fonts and paper styles that match your document's formality level
                        </Typography>
                      </Alert>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Design Theme</InputLabel>
                        <Select
                          value={formData.printable_config.design_theme}
                          label="Design Theme"
                          onChange={(e) => updatePrintableConfig('design_theme', e.target.value)}
                        >
                          <MenuItem value="classic">Classic Serif (Formal)</MenuItem>
                          <MenuItem value="modern">Modern Sans (Contemporary)</MenuItem>
                          <MenuItem value="heritage">Heritage Ornate (Ceremonial)</MenuItem>
                        </Select>
                      </FormControl>

                      <FormControl fullWidth size="small">
                        <InputLabel>Paper Effect</InputLabel>
                        <Select
                          value={formData.printable_config.paper_type}
                          label="Paper Effect"
                          onChange={(e) => updatePrintableConfig('paper_type', e.target.value)}
                        >
                          <MenuItem value="plain">Clean White</MenuItem>
                          <MenuItem value="parchment">Antique Parchment</MenuItem>
                          <MenuItem value="linen">Linen Texture</MenuItem>
                        </Select>
                      </FormControl>

                      {formData.printable_type === 'sticker' && (
                        <FormControl fullWidth size="small">
                          <InputLabel>Sticker Shape</InputLabel>
                          <Select
                            value={formData.printable_config.sticker_shape}
                            label="Sticker Shape"
                            onChange={(e) => updatePrintableConfig('sticker_shape', e.target.value)}
                          >
                            <MenuItem value="circle">Circle</MenuItem>
                            <MenuItem value="hexagon">Hexagon</MenuItem>
                            <MenuItem value="rounded-rect">Rounded Rectangle</MenuItem>
                          </Select>
                        </FormControl>
                      )}
                    </Box>

                    <Box sx={{ bgcolor: '#f8fafc', p: 2, borderRadius: 1, border: '1px solid #e2e8f0' }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', display: 'block', mb: 1 }}>
                        SECURITY FEATURES
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 3 }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={formData.printable_config.watermark}
                              onChange={(e) => updatePrintableConfig('watermark', e.target.checked)}
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>Apply Watermark</Typography>
                              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                                Subtle background pattern for anti-forgery
                              </Typography>
                            </Box>
                          }
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={formData.printable_config.security_seal}
                              onChange={(e) => updatePrintableConfig('security_seal', e.target.checked)}
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>Add Security Seal</Typography>
                              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                                QR code for digital verification
                              </Typography>
                            </Box>
                          }
                        />
                      </Box>
                    </Box>

                    <Divider />

                      <Box>
                        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Primary Brand Color</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                          Used for borders, headers, and branding elements
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <input
                            type="color"
                            value={formData.printable_config.primary_color}
                            onChange={(e) => updatePrintableConfig('primary_color', e.target.value)}
                            style={{ width: 60, height: 40, border: 'none', borderRadius: 8, cursor: 'pointer' }}
                          />
                          <TextField
                            size="small"
                            value={formData.printable_config.primary_color}
                            onChange={(e) => {
                              if (/^#[0-9A-F]{6}$/i.test(e.target.value)) {
                                updatePrintableConfig('primary_color', e.target.value);
                              }
                            }}
                            placeholder="#000000"
                            sx={{ width: 150 }}
                          />
                          <Typography variant="caption" color="text.secondary">
                            Click color box or enter hex code
                          </Typography>
                        </Box>
                      </Box>

                    <Divider />

                      <Box>
                        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Document Content</Typography>
                        <Alert severity="info" icon={<Info />} sx={{ fontSize: '0.75rem', mb: 2 }}>
                          <Typography variant="caption" fontWeight={600}>📝 How to use dynamic variables:</Typography>
                          <Typography variant="caption" sx={{ display: 'block', mt: 0.5, mb: 1 }}>
                            Variables are placeholders that get replaced with real data. Type them exactly as shown with double curly braces.
                          </Typography>
                          <Box component="div" sx={{ bgcolor: '#f1f5f9', p: 1, borderRadius: 1, fontFamily: 'monospace', fontSize: '0.7rem' }}>
                            <strong>Available Variables:</strong>
                            <br />• {'{{applicant_name}}'}  - Person's full name
                            <br />• {'{{business_name}}'}  - Business/company name
                            <br />• {'{{business_number}}'}  - Business registration number
                            <br />• {'{{business_type}}'}  - Type of business
                            <br />• {'{{business_category}}'}  - Business category
                            <br />• {'{{location}}'}  - Business location/address
                            <br />• {'{{service_name}}'}  - Name of this service
                            <br />• {'{{license_no}}'}  - License/reference number
                            <br />• {'{{expiry_date}}'}  - When it expires
                            <br />• {'{{date_issued}}'}  - Issue date
                            <br />• {'{{amount_paid}}'}  - Payment amount
                          </Box>
                        </Alert>
                        <TextField
                          fullWidth
                          multiline
                          rows={6}
                          value={formData.printable_config.document_content}
                          onChange={(e) => updatePrintableConfig('document_content', e.target.value)}
                          placeholder="Example: This certifies that {{applicant_name}} has paid {{amount_paid}} for {{service_name}}..."
                          helperText="Write your message and include variables where you want dynamic data to appear"
                        />
                      </Box>
                    </Box>

                    {/* Right Column: Live Preview */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, position: 'sticky', top: 20, height: 'fit-content' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="subtitle1" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Visibility fontSize="small" />
                          Live Preview
                        </Typography>
                        <Chip label="Sample Data" size="small" sx={{ bgcolor: '#e0f2fe', color: '#0369a1' }} />
                      </Box>

                      {/* Preview Document */}
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start' }}>
                        <Card sx={{
                          borderRadius: 2,
                          boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                          bgcolor: formData.printable_config.paper_type === 'parchment' ? '#faf8f3' : formData.printable_config.paper_type === 'linen' ? '#f5f5f0' : '#ffffff',
                          backgroundImage: formData.printable_config.watermark ? 'linear-gradient(45deg, rgba(0,0,0,0.02) 25%, transparent 25%, transparent 75%, rgba(0,0,0,0.02) 75%, rgba(0,0,0,0.02))' : 'none',
                          backgroundSize: '20px 20px',
                          border: `3px solid ${formData.printable_config.primary_color}`,
                          width: getPreviewDimensions().width,
                          aspectRatio: getPreviewDimensions().aspectRatio,
                          mx: 'auto',
                        }}>
                          <CardContent sx={{
                            p: getPreviewDimensions().padding,
                            fontSize: `${getPreviewDimensions().fontSize}rem`,
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column'
                          }}>
                          {(() => {
                            const s = getPreviewDimensions().scale;
                            return (
                              <>
                          {/* Logo & Header Section */}
                          <Box sx={{
                            textAlign: formData.printable_config.logo_position,
                            mb: 3 * s,
                            pb: 2 * s,
                            borderBottom: `${3 * s}px double ${formData.printable_config.primary_color}`
                          }}>
                            {countyProfile?.logo ? (
                              <Box
                                component="img"
                                src={countyProfile.logo}
                                alt="County Logo"
                                sx={{
                                  width: 60 * s,
                                  height: 60 * s,
                                  objectFit: 'contain',
                                  mb: 1 * s
                                }}
                              />
                            ) : (
                              <Box sx={{
                                width: 60 * s,
                                height: 60 * s,
                                borderRadius: '50%',
                                bgcolor: formData.printable_config.primary_color,
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mb: 1 * s
                              }}>
                                <Typography sx={{ color: 'white', fontWeight: 700, fontSize: `${1.5 * s}rem` }}>
                                  CG
                                </Typography>
                              </Box>
                            )}
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 800,
                                color: formData.printable_config.primary_color,
                                fontFamily: formData.printable_config.design_theme === 'classic' ? 'Georgia, serif' : formData.printable_config.design_theme === 'heritage' ? 'Times New Roman, serif' : 'Arial, sans-serif',
                                letterSpacing: '0.5px',
                                mt: 1 * s
                              }}
                            >
                              {countyProfile?.name ? countyProfile.name.toUpperCase() : 'COUNTY GOVERNMENT'}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, display: 'block', mt: 0.5 * s }}>
                              {formData.printable_type === 'license' ? 'DEPARTMENT OF TRADE & LICENSING' : formData.printable_type === 'certificate' ? 'DEPARTMENT OF HEALTH SERVICES' : 'COUNTY LICENSING AUTHORITY'}
                            </Typography>
                          </Box>

                          {/* Document Title */}
                          <Box sx={{ textAlign: 'center', mb: 3 * s, bgcolor: `${formData.printable_config.primary_color}15`, p: 2 * s, borderRadius: 1 * s }}>
                            <Typography
                              variant="h5"
                              sx={{
                                fontWeight: 800,
                                color: formData.printable_config.primary_color,
                                mb: 0.5 * s,
                                fontFamily: formData.printable_config.design_theme === 'classic' ? 'Georgia, serif' : formData.printable_config.design_theme === 'heritage' ? 'Times New Roman, serif' : 'Arial, sans-serif',
                                textTransform: 'uppercase',
                                letterSpacing: '1px'
                              }}
                            >
                              {printableTemplates[formData.printable_type]?.find(t => t.id === formData.printable_template)?.name || 'Select Template'}
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3 * s, mt: 1.5 * s }}>
                              <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b' }}>
                                Serial No: {formData.printable_type === 'license' ? 'UBP' : formData.printable_type === 'certificate' ? 'HC' : 'TAG'}/2024/001234
                              </Typography>
                              <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b' }}>
                                • Valid: {getPreviewDates().dateIssued} - {getPreviewDates().expiryDate}
                              </Typography>
                            </Box>
                          </Box>

                          {/* Document Content */}
                          <Box sx={{
                            mb: 4 * s,
                            p: 2 * s,
                            bgcolor: 'rgba(255,255,255,0.5)',
                            borderRadius: 1 * s,
                            minHeight: 120 * s
                          }}>
                            <Typography
                              variant="body2"
                              sx={{
                                lineHeight: 1.8,
                                fontFamily: formData.printable_config.design_theme === 'classic' ? 'Georgia, serif' : formData.printable_config.design_theme === 'heritage' ? 'Times New Roman, serif' : 'Arial, sans-serif',
                                fontSize: formData.printable_config.design_theme === 'heritage' ? '0.95rem' : '0.875rem'
                              }}
                              dangerouslySetInnerHTML={{
                                __html: formData.printable_config.document_content
                                  .replace(/\{\{applicant_name\}\}/g, '<strong>John Doe</strong>')
                                  .replace(/\{\{business_name\}\}/g, '<strong>Sample Business Ltd</strong>')
                                  .replace(/\{\{business_number\}\}/g, '<strong>BUS/20260406/0001</strong>')
                                  .replace(/\{\{business_type\}\}/g, '<strong>Sole Proprietorship</strong>')
                                  .replace(/\{\{business_category\}\}/g, '<strong>Retail Trade</strong>')
                                  .replace(/\{\{location\}\}/g, '<strong>Moi Avenue, Jubilee Building, Floor 2, Nairobi</strong>')
                                  .replace(/\{\{license_no\}\}/g, '<strong>LIC-2024-001</strong>')
                                  .replace(/\{\{expiry_date\}\}/g, `<strong>${getPreviewDates().expiryDate}</strong>`)
                                  .replace(/\{\{amount_paid\}\}/g, '<strong>KES 5,000</strong>')
                                  .replace(/\{\{date_issued\}\}/g, `<strong>${getPreviewDates().dateIssued}</strong>`)
                                  .replace(/\{\{service_name\}\}/g, `<strong>${formData.name || 'Sample Service'}</strong>`)
                              }}
                            />
                          </Box>

                          {/* Validity & Signature Section */}
                          <Box sx={{
                            bgcolor: '#f8fafc',
                            p: 2 * s,
                            borderRadius: 1 * s,
                            border: '1px solid #e2e8f0',
                            mb: 2 * s
                          }}>
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 * s, mb: 2 * s }}>
                              <Box>
                                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
                                  DATE OF ISSUE:
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {getPreviewDates().dateIssued}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
                                  VALID UNTIL:
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {getPreviewDates().expiryDate}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>

                          {/* Signatures */}
                          <Box sx={{
                            display: 'flex',
                            justifyContent: 'space-around',
                            gap: 2 * s,
                            mt: 2 * s,
                            mb: 1 * s
                          }}>
                            {signatories.length > 0 ? (
                              // Use selected signatories from dropdowns
                              (formData.printable_config.selected_signatory_ids || [])
                                .slice(0, parseInt(formData.printable_config.signature_count))
                                .map((signatoryId: number, idx: number) => {
                                  const signatory = signatories.find(s => s.id === signatoryId);
                                  if (!signatory) {
                                    return (
                                      <Box key={idx} sx={{ flex: 1, textAlign: 'center' }}>
                                        <Typography variant="caption" sx={{ fontStyle: 'italic', color: '#94a3b8', display: 'block', mb: 2 * s }}>
                                          [Select Signatory {idx + 1}]
                                        </Typography>
                                        <Box sx={{
                                          borderTop: `${2 * s}px solid #000`,
                                          mb: 0.5 * s,
                                          width: '90%',
                                          mx: 'auto'
                                        }} />
                                        <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', fontSize: `${0.7 * s}rem`, color: '#94a3b8' }}>
                                          Signatory {idx + 1}
                                        </Typography>
                                      </Box>
                                    );
                                  }
                                  return (
                                    <Box key={signatory.id} sx={{ flex: 1, textAlign: 'center' }}>
                                      {signatory.signature ? (
                                        <Box
                                          component="img"
                                          src={signatory.signature}
                                          alt={`${signatory.name} signature`}
                                          sx={{
                                            height: 40 * s,
                                            maxWidth: '90%',
                                            objectFit: 'contain',
                                            mb: 0.5 * s,
                                            mx: 'auto',
                                            display: 'block'
                                          }}
                                        />
                                      ) : (
                                        <Typography variant="caption" sx={{ fontStyle: 'italic', color: '#94a3b8', display: 'block', mb: 2 * s }}>
                                          [Signature]
                                        </Typography>
                                      )}
                                      <Box sx={{
                                        borderTop: `${2 * s}px solid #000`,
                                        mb: 0.5 * s,
                                        width: '90%',
                                        mx: 'auto'
                                      }} />
                                      <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', fontSize: `${0.7 * s}rem` }}>
                                        {signatory.name}
                                      </Typography>
                                      <Typography variant="caption" sx={{ color: '#64748b', fontSize: `${0.6 * s}rem`, display: 'block' }}>
                                        {signatory.title}
                                      </Typography>
                                    </Box>
                                  );
                                })
                            ) : (
                              // Fallback to manual configuration if no signatories
                              formData.printable_config.signature_titles.map((title, idx) => (
                                <Box key={idx} sx={{ flex: 1, textAlign: 'center' }}>
                                  <Typography variant="caption" sx={{ fontStyle: 'italic', color: '#94a3b8', display: 'block', mb: 2 * s }}>
                                    [Signature]
                                  </Typography>
                                  <Box sx={{
                                    borderTop: `${2 * s}px solid #000`,
                                    mb: 0.5 * s,
                                    width: '90%',
                                    mx: 'auto'
                                  }} />
                                  <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', fontSize: `${0.7 * s}rem` }}>
                                    {title}
                                  </Typography>
                                </Box>
                              ))
                            )}
                          </Box>

                          {/* QR Code & Security Seal Footer */}
                          <Box sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            pt: 2 * s,
                            borderTop: `${2 * s}px solid ${formData.printable_config.primary_color}`,
                            mt: 2 * s
                          }}>
                            {formData.printable_config.security_seal && (
                              <Box sx={{
                                width: 50 * s,
                                height: 50 * s,
                                border: `${2 * s}px solid ${formData.printable_config.primary_color}`,
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: `${formData.printable_config.primary_color}10`
                              }}>
                                <QrCode2 sx={{ fontSize: 30 * s, color: formData.printable_config.primary_color }} />
                              </Box>
                            )}

                            <Box sx={{ flex: 1, textAlign: 'center' }}>
                              <Typography variant="caption" sx={{ color: '#64748b', fontSize: `${0.65 * s}rem`, fontStyle: 'italic' }}>
                                This is an official county government document. Verify at verify.county.go.ke
                              </Typography>
                            </Box>

                            {formData.printable_config.security_seal && (
                              <Box sx={{
                                textAlign: 'right',
                                opacity: 0.6
                              }}>
                                <Typography variant="caption" sx={{ fontSize: `${0.6 * s}rem`, color: '#94a3b8' }}>
                                  OFFICIAL SEAL
                                </Typography>
                              </Box>
                            )}
                          </Box>
                              </>
                            );
                          })()}
                        </CardContent>
                      </Card>
                      </Box>

                      <Box sx={{ textAlign: 'center', mt: 1, mb: 2 }}>
                        <Chip
                          label={`Document Size: ${formData.printable_config.document_size.toUpperCase()}`}
                          size="small"
                          sx={{ bgcolor: '#f1f5f9', color: '#64748b', fontWeight: 600 }}
                        />
                      </Box>

                      <Alert severity="info" icon={false} sx={{ fontSize: '0.75rem' }}>
                        <Typography variant="caption" fontWeight={600}>💡 Preview Tips:</Typography>
                        <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                          • Changes update instantly
                          <br />• Sample data shows how variables will appear
                          <br />• Final document will use actual applicant data
                        </Typography>
                      </Alert>
                    </Box>
                  </Box>
                )}
              </Box>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
              <Button
                onClick={() => navigate(`/modules/${id}/services`)}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {activeStep > 0 && (
                  <Button onClick={handleBack} variant="outlined" disabled={submitting}>
                    Back
                  </Button>
                )}
                {activeStep < steps.length - 1 ? (
                  <Button
                    onClick={handleNext}
                    variant="contained"
                    sx={{ background: colors.gradients.primary }}
                  >
                    Next
                  </Button>
                ) : (
                  <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={submitting}
                    sx={{ background: colors.gradients.primary }}
                  >
                    {submitting ? <CircularProgress size={20} /> : 'Update Service'}
                  </Button>
                )}
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </ModuleLayout>
  );
};
